import {
  activityFromTicker,
  fetchAuctionBidHistory,
  fetchMyActivityFeed,
  isOwnEntry,
  mergeBidEntries,
  ownSeqFromYou,
  type AuctionBidHistoryEntry,
  type SaleFeed,
  type SaleFeedStatusInfo,
} from '@auctisync/api-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFeedEvent } from '../../hooks/useSaleFeed';
import type { MultiSaleLive } from '../../hooks/useSaleFeeds';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';
import type { SaleStandingCounts } from './SaleAside';

/**
 * The two panels beside a sale — "Your standing" and "Activity" — LIVE
 * (2026-10-11). One rule for every page that draws them beside one sale: the
 * Live Room, the lot page and Home.
 *
 * Until today only the Live Room's panels moved. The lot page and Home read
 * both once (`useSaleAsideData`, removed), and the only refresh they ever got was by
 * accident — a re-read whenever Supabase handed over a new session object,
 * i.e. each time the bidder came back to the tab. And under record-at-close
 * (2026-10-08) the database holds an open lot's bids only once it closes, so
 * re-reading it could never have shown a live sale's bids anyway. Jim's rule:
 * *"Lot pages have to be live. Bids should update without having to refresh."*
 *
 * WHERE EACH NUMBER COMES FROM — the Live Room's rules, moved here unchanged:
 *   - ACTIVITY, on the engine: the database's recent bids once, as the backlog
 *     (so the panel is not empty on open), again after a dropped feed comes
 *     back (what was missed), and every bid after that from the engine's own
 *     `ticker` the moment it lands — so the page's feed must ask for it
 *     (`ticker: true`). `mergeBidEntries` shows a bid in both once. On the
 *     Supabase book: the database every 10 s while the site can reach it.
 *   - STANDING: the counts are SQL's (`get_my_standing_counts`, the one place
 *     "winning / outbid / watching" is counted), read once per sale and
 *     viewer, and again 1.5 s after the engine says the bidder's own standing
 *     moved — once per burst, not per bid. The engine's notifier keeps the
 *     positions they count current. On the Supabase book: with the 10 s read.
 *   - "You": a line is the bidder's when the database says so or a `you`
 *     carried its sequence (`isOwnEntry`).
 *
 * `lotNumbers`: the ticker knows lots by id. A page that already holds the
 * sale's lots passes their numbers; the rest are looked up as they turn up
 * (`useNumberedLines`).
 */

const ACTIVITY_LINES = 20;
/** Ticker lines are drawn together, at most this often (ms). */
const TICKER_EVERY_MS = 150;
/** The counts are asked again once a burst of the bidder's own changes is over. */
const COUNTS_AFTER_MS = 1_500;
/** A sale on the Supabase book: the database read again this often (ms). */
const BOOK_EVERY_MS = 10_000;

type ActivityLine = AuctionBidHistoryEntry & { seq?: string };

export interface LiveSaleAsideInput {
  auctionId: string | null;
  /** The page's own feed for the sale — asked with `ticker: true`. Null off the engine. */
  feed: SaleFeed | null;
  /** Where the sale's live updates come from (`useLiveSource`); null until known. */
  sourceKind: 'engine' | 'supabase' | null;
  feedStatus: SaleFeedStatusInfo | null;
  /** The site cannot reach the server, or the feed is down: read nothing. */
  offline: boolean;
  /** Lot id → lot number for this sale, when the page holds them. */
  lotNumbers?: ReadonlyMap<string, number> | undefined;
}

/** A lookup gathers the lots that turned up in this long a burst (ms). */
const LOOKUP_AFTER_MS = 250;

/**
 * Live lines are drawn under their LOT NUMBER, and the engine knows lots by
 * id. The page's own rows number most of them; the rest are looked up as they
 * turn up, a few ids at a time — never a whole sale read to number a handful
 * of lines (that read cost ~30 KB per page open on a DTA-size sale, at every
 * open: caught 2026-10-11 before it shipped). A lot that comes back without a
 * number is hidden from the catalogue: no line, as on the sale's own page.
 *
 * Returns `push`: lines whose `lotNumber` is still to be filled. Each reaches
 * `add` numbered, as soon as its number is known.
 */
function useNumberedLines(
  known: ReadonlyMap<string, number> | undefined,
  add: (lines: ActivityLine[]) => void,
): (lines: readonly ActivityLine[]) => void {
  const knownRef = useRef(known);
  knownRef.current = known;
  const addRef = useRef(add);
  addRef.current = add;
  const looked = useRef(new Map<string, number>());
  const pending = useRef<ActivityLine[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const numberOf = (lotId: string | undefined): number | undefined =>
    lotId === undefined ? undefined : (knownRef.current?.get(lotId) ?? looked.current.get(lotId));

  const flush = useCallback(() => {
    const ready: ActivityLine[] = [];
    const waiting: ActivityLine[] = [];
    for (const line of pending.current) {
      const n = numberOf(line.lotId);
      if (n === undefined) waiting.push(line);
      else ready.push({ ...line, lotNumber: n });
    }
    pending.current = waiting;
    if (ready.length > 0) addRef.current(ready);
  }, []);

  const lookUp = useCallback(() => {
    if (timer.current !== null) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      const unknown = [
        ...new Set(
          pending.current
            .filter((l) => numberOf(l.lotId) === undefined)
            .map((l) => l.lotId)
            .filter((x): x is string => x !== undefined),
        ),
      ];
      if (unknown.length === 0) {
        flush();
        return;
      }
      void (async () => {
        const { data } = await supabase.from('lots').select('id, lot_number').in('id', unknown);
        for (const r of (data ?? []) as { id: string; lot_number: number }[]) {
          looked.current.set(r.id, r.lot_number);
        }
        flush();
        // Asked and still unnumbered = hidden: dropped. A line that arrived
        // during the read is kept, and its lot asked for in turn.
        const asked = new Set(unknown);
        pending.current = pending.current.filter(
          (l) => l.lotId === undefined || !asked.has(l.lotId),
        );
        if (pending.current.length > 0) lookUp();
      })();
    }, LOOKUP_AFTER_MS);
  }, [flush]);

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  return useCallback(
    (lines: readonly ActivityLine[]) => {
      pending.current.push(...lines);
      flush();
      if (pending.current.length > 0) lookUp();
    },
    [flush, lookUp],
  );
}

/** `get_my_standing_counts`' one row, or zeroes when it answered nothing. */
export function countsFrom(
  data: readonly { winning: number; outbid: number; watching: number; sales: number }[] | null,
): SaleStandingCounts {
  const row = data?.[0];
  return row
    ? { winning: row.winning, outbid: row.outbid, watching: row.watching, sales: row.sales }
    : { winning: 0, outbid: 0, watching: 0, sales: 0 };
}

export function useLiveSaleAside(input: LiveSaleAsideInput): {
  standing: SaleStandingCounts | null;
  events: AuctionBidHistoryEntry[];
} {
  const { auctionId, feed, sourceKind, feedStatus, offline } = input;
  const { userId } = useSession();
  const [events, setEvents] = useState<ActivityLine[]>([]);
  const [ownSeqs, setOwnSeqs] = useState<ReadonlySet<string>>(new Set());
  const [standing, setStanding] = useState<SaleStandingCounts | null>(null);

  // ── activity: the backlog, the ticker, the Supabase book's read ──
  const saleRef = useRef(auctionId);
  saleRef.current = auctionId;
  const loadBacklog = useCallback(async (saleId: string) => {
    const res = await fetchAuctionBidHistory(supabase, saleId, ACTIVITY_LINES);
    // A slow answer for a sale the page has since left is not this sale's.
    if (saleRef.current !== saleId) return;
    setEvents((prev) => mergeBidEntries([res.entries, prev], ACTIVITY_LINES));
  }, []);

  useEffect(() => {
    setEvents([]);
    setOwnSeqs(new Set());
  }, [auctionId, userId]);

  // On the engine: once, when the page knows the sale is the engine's.
  useEffect(() => {
    if (auctionId === null || sourceKind !== 'engine') return;
    void loadBacklog(auctionId);
  }, [auctionId, sourceKind, userId, loadBacklog]);

  // Back from a drop: read what was missed.
  const wasDown = useRef(false);
  const status = feedStatus?.status;
  useEffect(() => {
    if (status === 'reconnecting' || status === 'paused') wasDown.current = true;
    if (status === 'live' && wasDown.current) {
      wasDown.current = false;
      if (auctionId !== null) void loadBacklog(auctionId);
    }
  }, [status, auctionId, loadBacklog]);

  const pushLines = useNumberedLines(input.lotNumbers, (lines) => {
    setEvents((prev) => mergeBidEntries([lines, prev], ACTIVITY_LINES));
  });
  const tickerBuffer = useRef<ActivityLine[]>([]);
  const tickerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useFeedEvent(feed, 'ticker', (entry) => {
    // Numbered by `useNumberedLines`; 0 until then.
    tickerBuffer.current.push(activityFromTicker(entry, 0));
    if (tickerTimer.current !== null) return;
    tickerTimer.current = setTimeout(() => {
      tickerTimer.current = null;
      const lines = tickerBuffer.current;
      tickerBuffer.current = [];
      pushLines(lines);
    }, TICKER_EVERY_MS);
  });

  // ── standing: SQL's counts, once and after the bidder's own changes ──
  const [countsAsked, setCountsAsked] = useState(0);
  const countsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useFeedEvent(feed, 'you', (you) => {
    const seq = ownSeqFromYou(you);
    if (seq !== null) setOwnSeqs((prev) => (prev.has(seq) ? prev : new Set(prev).add(seq)));
    if (countsTimer.current !== null) clearTimeout(countsTimer.current);
    countsTimer.current = setTimeout(() => {
      countsTimer.current = null;
      setCountsAsked((n) => n + 1);
    }, COUNTS_AFTER_MS);
  });

  useEffect(
    () => () => {
      if (tickerTimer.current !== null) clearTimeout(tickerTimer.current);
      if (countsTimer.current !== null) clearTimeout(countsTimer.current);
    },
    [],
  );

  const loadCounts = useCallback(async (saleId: string) => {
    const { data } = await supabase.rpc('get_my_standing_counts', { p_auction_id: saleId });
    if (saleRef.current === saleId) setStanding(countsFrom(data));
  }, []);

  useEffect(() => {
    if (auctionId === null || userId === null) {
      setStanding(null);
      return;
    }
    void loadCounts(auctionId);
  }, [auctionId, userId, countsAsked, loadCounts]);

  // On the Supabase book: both again, every 10 s, while the site can reach it.
  useEffect(() => {
    if (auctionId === null || sourceKind !== 'supabase' || offline) return;
    void loadBacklog(auctionId);
    const id = setInterval(() => {
      void loadBacklog(auctionId);
      if (userId !== null) void loadCounts(auctionId);
    }, BOOK_EVERY_MS);
    return () => {
      clearInterval(id);
    };
  }, [auctionId, sourceKind, offline, userId, loadBacklog, loadCounts]);

  const marked = useMemo<AuctionBidHistoryEntry[]>(
    () => events.map((e) => (isOwnEntry(e, ownSeqs) === e.isYou ? e : { ...e, isYou: true })),
    [events, ownSeqs],
  );

  return { standing: userId === null ? null : standing, events: marked };
}

// ─── Every sale the bidder is in: the Watchlist ────────────────────────────

/**
 * The same two panels for the WATCHLIST, which spans sales (Jim, 2026-09-18:
 * *"Shouldn't it describe all of the sales that the buyer is involved in?"*),
 * LIVE (2026-10-11).
 *
 * The page already holds one connection per sale with an open watched lot
 * (`useSaleFeeds`), and the engine sends each the lots the bidder watches and
 * the ones they bid on — exactly "the lots you are involved in". So:
 *   - ACTIVITY: the database's feed once (`get_my_activity_feed`) as the
 *     backlog, again after a dropped feed comes back; then a line for each bid
 *     those connections report — a lot whose bid count went up, at the price
 *     and moment the engine gives. No sale-wide ticker: nothing is sent that
 *     the page does not already receive. A bid the engine names as the
 *     bidder's own (`you`, same sequence) is marked "You". On the Supabase
 *     book, a Realtime row asks the database again (it holds those bids).
 *   - STANDING: SQL's counts across every sale, once, and again 1.5 s after
 *     the bidder's own standing moves (or a book row arrives).
 * Lot numbers: the page's own rows; a lot it does not list (bid on, not
 * watched) is looked up as it turns up (`useNumberedLines`).
 */
export function useLiveEverywhereAside(input: {
  live: MultiSaleLive;
  lotNumbers: ReadonlyMap<string, number>;
}): { standing: SaleStandingCounts | null; events: AuctionBidHistoryEntry[] } {
  const { live } = input;
  const { userId } = useSession();
  const [events, setEvents] = useState<ActivityLine[]>([]);
  const [ownSeqs, setOwnSeqs] = useState<ReadonlySet<string>>(new Set());
  const [standing, setStanding] = useState<SaleStandingCounts | null>(null);
  const viewerRef = useRef(userId);
  viewerRef.current = userId;

  const loadBacklog = useCallback(async () => {
    const asked = viewerRef.current;
    if (asked === null) return;
    const { entries } = await fetchMyActivityFeed(supabase, ACTIVITY_LINES);
    if (viewerRef.current === asked) {
      setEvents((prev) => mergeBidEntries([entries, prev], ACTIVITY_LINES));
    }
  }, []);
  const loadCounts = useCallback(async () => {
    const asked = viewerRef.current;
    if (asked === null) return;
    const { data } = await supabase.rpc('get_my_standing_counts', {});
    if (viewerRef.current === asked) setStanding(countsFrom(data));
  }, []);

  useEffect(() => {
    setEvents([]);
    setOwnSeqs(new Set());
    setStanding(null);
    if (userId === null) return;
    void loadBacklog();
    void loadCounts();
  }, [userId, loadBacklog, loadCounts]);

  // Back from a drop on any sale: read what was missed.
  const statusKey = live
    .statuses()
    .map((s) => `${s.saleId}:${s.info.status}`)
    .join('|');
  const wasDown = useRef(false);
  useEffect(() => {
    const parts = statusKey.split('|');
    if (parts.some((p) => p.endsWith(':reconnecting') || p.endsWith(':paused'))) {
      wasDown.current = true;
      return;
    }
    if (wasDown.current && parts.every((p) => p === '' || p.endsWith(':live'))) {
      wasDown.current = false;
      void loadBacklog();
      void loadCounts();
    }
  }, [statusKey, loadBacklog, loadCounts]);

  const pushLines = useNumberedLines(input.lotNumbers, (lines) => {
    setEvents((prev) => mergeBidEntries([lines, prev], ACTIVITY_LINES));
  });

  // ── the live events, across every connection ──
  const countsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const askCountsSoon = useCallback(() => {
    if (countsTimer.current !== null) clearTimeout(countsTimer.current);
    countsTimer.current = setTimeout(() => {
      countsTimer.current = null;
      void loadCounts();
    }, COUNTS_AFTER_MS);
  }, [loadCounts]);
  const bookTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const offYou = live.on('you', (you) => {
      const seq = ownSeqFromYou(you);
      if (seq !== null) setOwnSeqs((prev) => (prev.has(seq) ? prev : new Set(prev).add(seq)));
      askCountsSoon();
    });
    const offLots = live.on('lots', (changes) => {
      const lines: ActivityLine[] = [];
      for (const { lot, prev } of changes) {
        // First sight is the backlog's to show; a line is a bid that just landed.
        if (prev === undefined) continue;
        if ((lot.bid_count ?? 0) <= (prev.bid_count ?? 0)) continue;
        lines.push({
          id: `lot:${lot.lot_id}:${lot.seq}`,
          lotId: lot.lot_id,
          lotNumber: 0,
          seq: lot.seq,
          amountCents: lot.current_high_cents,
          createdAt: lot.last_bid_at ?? new Date().toISOString(),
          isYou: false,
          isProxy: false,
        });
      }
      if (lines.length > 0) pushLines(lines);
    });
    const offRow = live.on('row', () => {
      // The Supabase book: the database holds these bids — ask it, once a burst ends.
      if (bookTimer.current !== null) clearTimeout(bookTimer.current);
      bookTimer.current = setTimeout(() => {
        bookTimer.current = null;
        void loadBacklog();
        void loadCounts();
      }, COUNTS_AFTER_MS);
    });
    return () => {
      offYou();
      offLots();
      offRow();
    };
  }, [live.on, askCountsSoon, pushLines, loadBacklog, loadCounts]);

  useEffect(
    () => () => {
      for (const t of [countsTimer, bookTimer]) {
        if (t.current !== null) clearTimeout(t.current);
      }
    },
    [],
  );

  const marked = useMemo<AuctionBidHistoryEntry[]>(
    () => events.map((e) => (isOwnEntry(e, ownSeqs) === e.isYou ? e : { ...e, isYou: true })),
    [events, ownSeqs],
  );
  return { standing: userId === null ? null : standing, events: marked };
}
